import { EXTERNAL_SCOPE_TYPES } from '../../models/messaging-user';
import { MESSAGE_STATUSES, TEXT_MAX_LENGTH } from '../../models/messaging-message';
import { MODEL_NAMES } from '../../constants/index';
import { and, not } from 'ember-truth-helpers';
import { filterSystemAndCurrentUser } from '../../utils/messaging/user';
import { fn } from '@ember/helper';
import { getMessageDateLabel } from '../../utils/messaging/format-date';
import { isTest } from 'ember-simplepractice/utils/is-testing';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task, timeout } from 'ember-concurrency';
import { waitForNext } from 'ember-simplepractice/utils/waiters';
import BaseRoute from './base-route.gjs';
import CharacterCounter from 'ember-simplepractice/components/spds/core/character-counter';
import Component from '@glimmer/component';
import ConversationHeader from '../conversation-header.gjs';
import ConversationMessage from '../conversation-message.gjs';
import DraftConversationBanner from '../draft-conversation-banner.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import InfinityModel from 'ember-infinity/lib/infinity-model';
import MessagingCarousel from '../messaging-carousel.gjs';
import MessagingNotification from '../messaging-notification.gjs';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import generateUUID from 'ember-simplepractice/utils/generate-uuid';
import styles from './conversation.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import textareaAutosize from 'ember-simplepractice/modifiers/textarea-autosize';

const MESSAGE_READ_THROTTLE = isTest() ? 0 : 2500;
const MESSAGE_READ_OFFSET = -50;

export default class MessagingRoutesConversation extends Component {
  @service messaging;
  @service messagingLogging;
  @service messagingEvents;
  @service messagingNavigation;
  @service clientProfileNavigator;
  @service('websocket/messagingWebsocket') messagingWebsocket;
  @service store;
  @service intl;
  @service('spds/toast') toast;

  chatElement;
  messageMaxLength = TEXT_MAX_LENGTH;

  onInsert = modifier(async e => {
    this.chatElement = e;
    if (this.shouldMarkAsRead) {
      this.readConversation.perform();
    }
  });

  get routeHandler() {
    return this.args.handler;
  }

  get conversation() {
    return this.routeHandler.conversation;
  }

  get messages() {
    return this.routeHandler.messages;
  }

  get messagesByDate() {
    const groupedMessagesMap = new Map();
    this.messages?.forEach(message => {
      let dateKey = new Date(message.createdAt).toDateString();

      if (!groupedMessagesMap.has(dateKey)) {
        groupedMessagesMap.set(dateKey, []);
      }

      groupedMessagesMap.get(dateKey).unshift(message);
    });
    return groupedMessagesMap;
  }

  get reversedDates() {
    return Array.from(this.messagesByDate.entries())
      .map(([date]) => date)
      .reverse();
  }

  get filteredUsers() {
    return filterSystemAndCurrentUser(
      this.conversation?.users,
      this.messaging.currentProfile?.user
    );
  }

  get emptyStateSubheader() {
    return this.intl.t('emc.start_conversation_callout', {
      name: this.filteredUsers.map(user => user.name).join(' & '),
    });
  }

  get clinicianUser() {
    return this.filteredUsers.find(user => user.externalScope === EXTERNAL_SCOPE_TYPES.user);
  }

  get isLoading() {
    return this.routeHandler.isVisiting || this.routeHandler.getMessages.isRunning;
  }

  get showEmptyState() {
    return this.messages.length === 0 && !this.showDraftBanner;
  }

  get showDraftBanner() {
    return this.groupMessagingEnabled && this.conversation?.isDraft;
  }

  get hasDisplayName() {
    return Boolean(this.conversation?.displayName?.trim());
  }

  get canModifyMembers() {
    return this.conversation?.isGroup;
  }

  get canViewProfile() {
    return (
      !this.conversation?.isGroup &&
      this.clientProfileNavigator.canNavigateToClientProfile(
        this.filteredUsers,
        this.messaging.currentProfile?.isClinician
      )
    );
  }

  get showInactiveReasons() {
    return this.routeHandler.inactiveReasons.length > 0;
  }

  get groupMessagingEnabled() {
    return this.messaging.groupMessagingEnabled;
  }

  get awayMessage() {
    return this.clinicianUser?.awayMessageText;
  }

  get showAwayIndicator() {
    return this.messaging.currentProfile?.isClient && this.clinicianUser?.isAway;
  }

  get isSendDisabled() {
    const websocketNotConnected = !this.messagingWebsocket.isConnected;
    const hasInactiveReasons = this.routeHandler.inactiveReasons.length > 0;
    const isSavingNewConversation = this.routeHandler.saveNewConversation.isRunning;
    return this.isLoading || isSavingNewConversation || websocketNotConnected || hasInactiveReasons;
  }

  get isSendButtonDisabled() {
    const messageLength = this.conversation?.messageDraft?.trim().length || 0;
    return this.isSendDisabled || !messageLength || messageLength > this.messageMaxLength;
  }

  get shouldMarkAsRead() {
    const mostRecentMessageInViewport = this.chatElement.scrollTop >= MESSAGE_READ_OFFSET;
    const hasUnreadConversation = this.messaging.currentProfile.unreadConversationIds?.includes(
      this.conversation.id
    );
    return mostRecentMessageInViewport && hasUnreadConversation;
  }

  get hasInfinityModel() {
    return this.messages instanceof InfinityModel;
  }

  handleTextareaInput = ({ target: textarea }) => {
    this.conversation.messageDraft = textarea.value;
  };

  handleKeyDown = event => {
    const { key, shiftKey } = event;

    if (key === 'Enter' && !shiftKey) {
      event.preventDefault();
      this.onSend(this.conversation.messageDraft);
    }
  };

  async sendMessage(text) {
    if (this.isSendDisabled) return;

    const message = this.store.createRecord(MODEL_NAMES.MESSAGE, {
      conversation: this.conversation,
      createdAt: new Date(),
      isOwn: true,
      localId: generateUUID(),
      status: MESSAGE_STATUSES.SENDING,
      text: text.trim(),
      user: this.messaging.currentProfile.user,
    });

    try {
      this.messages.unshiftObject(message);

      if (this.conversation.isNew) {
        await this.routeHandler.saveNewConversation.perform(this.conversation, message);
      }

      await message.save();
      this.messagingLogging.trackEvent('emc.tracking_event_names.message_sent');
      message.status = MESSAGE_STATUSES.DELIVERED;
    } catch (error) {
      this.#handleSendMessageError(message, error);
    }
  }

  #handleSendMessageError(message, error) {
    const isDisabledConversationError = error.errors?.some(
      error => error.id === 'base#conversation_was_disabled'
    );

    message.status = MESSAGE_STATUSES.FAILED;

    if (isDisabledConversationError) {
      this.toast.error(this.intl.t('emc.disabled_conversation_error'));
    }
  }

  readConversation = task({ drop: true }, async () => {
    this.messagingWebsocket.subscription?.perform('read_at', {
      'conversation_id': this.conversation.id,
    });
    await timeout(MESSAGE_READ_THROTTLE);
  });

  onSend = async text => {
    if (this.isSendButtonDisabled) return;

    this.conversation.messageDraft = '';

    // textarea-autosize modifier does not fire when submit button is
    // clicked so we need to reset it manually.
    document.querySelector('#message-textarea').style.height = '';

    await this.sendMessage(text);
  };

  resendMessage = async failedMessage => {
    this.messages.removeObject(failedMessage);
    await this.sendMessage(failedMessage.text);
  };

  getMessagesForDate = date => {
    return this.messagesByDate.get(date);
  };

  loadMoreMessages = async model => {
    if (!model.canLoadMore) return;

    let previousScrollTop = this.chatElement.scrollTop;
    await this.routeHandler.loadMoreMessages.perform(model);
    await waitForNext(); // Waiting for the DOM to update before restoring scroll position
    this.chatElement.scrollTop = previousScrollTop;
  };

  close = async () => {
    await this.messagingNavigation.goToPreviousRoute();
  };

  onNameConversation = () => {};

  onModifyMembers = () => {};

  navigateToClientProfileTask = task({ drop: true }, async () => {
    try {
      await this.clientProfileNavigator.navigateToClientProfile(
        this.conversation,
        this.filteredUsers
      );
    } catch (error) {
      this.toast.error(this.intl.t('emc.general_error'));
    }
  });

  onViewProfile = () => {
    document.activeElement.blur();
    this.navigateToClientProfileTask.perform();
  };

  retryConnection = () => {
    this.messagingWebsocket.connectSocket();
  };

  onScroll = () => {
    if (this.shouldMarkAsRead) {
      this.readConversation.perform();
    }
  };

  <template>
    <BaseRoute class="{{styles.component}} conversation-page" data-test-conversation-route>
      <:header>
        <GhostButton
          @fitContent={{true}}
          @iconOnly={{true}}
          {{on "click" this.close}}
          class="back-button"
          data-test-conversation-page-back-button
        >
          {{svgJar "messaging-chevron-left"}}
        </GhostButton>

        <ConversationHeader
          @conversation={{this.conversation}}
          @filteredUsers={{this.filteredUsers}}
          @showAwayIndicator={{this.showAwayIndicator}}
        />
      </:header>
      <:default>
        <div
          class="content"
          data-test-conversation-page-content
          tabindex="0"
          {{this.onInsert}}
          {{on "scroll" this.onScroll}}
        >
          {{#if this.isLoading}}
            <div class="spinner" data-test-loading-spinner>
              {{svgJar "messaging-spinner"}}
            </div>
          {{else if this.showEmptyState}}
            <div
              class="empty-state {{if this.showAwayIndicator 'clinician-away'}}"
              data-test-empty-state
            >
              {{svgJar "messaging-empty-conversation"}}

              <h3>{{t "emc.start_conversation_header"}}</h3>
              <p>{{this.emptyStateSubheader}}</p>
            </div>
          {{else}}
            <div class="messages">
              {{#if this.hasInfinityModel}}
                <SharedInfinityLoader
                  @model={{this.messages}}
                  @infinityLoad={{this.loadMoreMessages}}
                  @triggerOffset={{0}}
                />
              {{/if}}
              {{#each this.reversedDates as |date|}}
                <div class="date-label">{{getMessageDateLabel date}}</div>
                {{#each (this.getMessagesForDate date) as |message|}}
                  <ConversationMessage
                    @conversation={{this.conversation}}
                    @message={{message}}
                    @onResend={{this.resendMessage}}
                  />
                {{/each}}
              {{/each}}
            </div>
            {{#if this.showDraftBanner}}
              <div class="conversation-banners" data-test-conversation-banners>
                <DraftConversationBanner
                  @showNameConversationAction={{and
                    this.canModifyMembers
                    (not this.hasDisplayName)
                  }}
                  @showModifyMembersAction={{this.canModifyMembers}}
                  @showViewProfileAction={{this.canViewProfile}}
                  @onNameConversation={{this.onNameConversation}}
                  @onModifyMembers={{this.onModifyMembers}}
                  @onViewProfile={{this.onViewProfile}}
                />
              </div>
            {{/if}}
          {{/if}}
        </div>
      </:default>
      <:footer>
        {{#if this.messagingWebsocket.isConnected}}
          {{#if this.showInactiveReasons}}
            {{#if this.groupMessagingEnabled}}
              <MessagingCarousel
                @ariaLive="polite"
                @ariaLabel={{t "emc.carousel.inactive_reasons_label"}}
                @items={{this.routeHandler.inactiveReasons}}
                data-test-inactive-reasons-carousel
                as |reason|
              >
                <MessagingNotification
                  @type="info"
                  @message={{reason.message}}
                  @buttonLabel={{reason.action}}
                  @onButtonClick={{fn this.messagingEvents.emit reason.handler reason.meta}}
                />
              </MessagingCarousel>
            {{else}}
              {{#each this.routeHandler.inactiveReasons as |reason|}}
                <MessagingNotification
                  @type="info"
                  @message={{reason.message}}
                  @buttonLabel={{reason.action}}
                  @onButtonClick={{fn this.messagingEvents.emit reason.handler reason.meta}}
                />
              {{/each}}
            {{/if}}
          {{else if this.showAwayIndicator}}
            <MessagingNotification @type="away" @message={{this.awayMessage}} @truncated={{true}} />
          {{/if}}
        {{else}}
          <MessagingNotification
            @type="error"
            @message={{t "emc.device_offline_error"}}
            @buttonLabel={{t "emc.retry_connection"}}
            @onButtonClick={{this.retryConnection}}
          />
        {{/if}}
        <div class="input-area">
          <textarea
            id="message-textarea"
            class="text-input"
            value={{this.conversation.messageDraft}}
            placeholder={{t "emc.message_placeholder"}}
            {{on "input" this.handleTextareaInput}}
            {{on "keydown" this.handleKeyDown}}
            {{textareaAutosize this.conversation.messageDraft}}
            data-test-message-input
          />

          <button
            disabled={{this.isSendButtonDisabled}}
            class="button primary send-button"
            type="button"
            data-test-message-send
            {{on "click" (fn this.onSend this.conversation.messageDraft)}}
          >
            {{t "emc.send"}}
          </button>
        </div>
        <div class="input-sub-text">
          <span data-test-hint>
            <strong>{{t "emc.new_line_hint.keys"}}</strong>
            {{t "emc.new_line_hint.effect"}}
          </span>
          <CharacterCounter
            @limit={{this.messageMaxLength}}
            @value={{this.conversation.messageDraft}}
          />
        </div>
      </:footer>
    </BaseRoute>
  </template>
}
